import type Game from '../../../scenes/Game';
import { canPlayersCommunicate } from '../../../services/VirtualRoomProximityHelper';
import { DEFAULT_PROXIMITY_FALLBACK_PX } from '../../../constants/proximity';
import { virtualRoomAreaService } from '../../../services/VirtualRoomAreaService';
import { getPlayerPositionForMediaProximity } from '../../../services/getPlayerPositionForMediaProximity';
import type { VirtualRoomFilterEntry } from '../../../stores/VirtualRoomFilterStore';
import {
  hasServerVirtualRoomCoOccupancy,
  resolveSameVirtualRoomMedia,
  isPasswordOkForVirtualRoomPair,
  isLocalPlayerVirtualRoomAuthorized,
  getLocalServerVirtualRoomId,
  getRemoteServerVirtualRoomId,
  shouldExcludeByServerVirtualRoom,
} from './resolveSameVirtualRoomMedia';

export interface ConferenceMediaEligibilityOptions {
  isScreenSharing?: boolean;
  /** Settle grace: include briefly while position data is in-flight (open-world only). */
  settleGraceActive?: boolean;
}

/**
 * Single eligibility gate for sidebar, overlays, subscriptions, and screen share.
 * Painted Virtual Room co-occupants are included without distance checks when
 * proximityDistance is unset / non-positive / Infinity on the room.
 */
export function resolveConferenceMediaEligibility(
  game: Game | null | undefined,
  participantIdentity: string,
  virtualRoomFilters: Record<string, VirtualRoomFilterEntry>,
  options?: ConferenceMediaEligibilityOptions
): boolean {
  if (!game?.myPlayer || !participantIdentity) return false;

  // Hard gate: if the server says the LOCAL player is inside a locked room they are not authorized
  // for, they are eligible for no one's media (sidebar, overlays, or subscription) until they enter
  // the password. Server-authoritative, so it holds even when the filter snapshot is stale. [SC-246]
  if (!isLocalPlayerVirtualRoomAuthorized(game)) return false;

  const myPlayer = game.myPlayer;
  const mySid = game.network?.mySessionId || '';
  const myArea = virtualRoomAreaService.isPlayerInArea(myPlayer.x, myPlayer.y);

  const otherPos = getPlayerPositionForMediaProximity(game, participantIdentity);
  const otherArea = otherPos
    ? virtualRoomAreaService.isPlayerInArea(otherPos.x, otherPos.y)
    : null;

  // Strict client-side boundary isolation: visual boundaries must be respected exactly.
  if (myArea && otherArea && myArea.roomId !== otherArea.roomId) {
    return false;
  }
  // "One inside a room, the other not" — normally isolate. BUT on a join the two
  // clients' area detection resolves asymmetrically: our own area resolves seconds
  // before the peer's (or vice-versa), leaving one side null even though the server
  // authoritatively places both in the same virtual room. That transient asymmetry
  // used to hard-exclude media here — blocking BOTH the video eligibility and the
  // track subscription (this fn gates both) until the peer's client-side area finally
  // caught up, i.e. the SC-273 join delay. When the server/roster confirms co-occupancy,
  // treat the known (client-declared) area as shared and defer to its password gate
  // instead of excluding. Isolation is preserved: a genuine "just outside the boundary"
  // peer is never reported as a co-occupant by the server, and locked rooms still
  // require both sessions to be authorized — the password gate is keyed by
  // `myArea.roomId`, the same virtual-room id `virtualRoomFilters` is keyed by.
  if ((myArea && !otherArea) || (!myArea && otherArea)) {
    const knownRoomId = myArea?.roomId || otherArea?.roomId;
    const serverConfirmsCoOccupant =
      hasServerVirtualRoomCoOccupancy(game, participantIdentity, virtualRoomFilters) ||
      !!game.network?.isVirtualRoomCoOccupant?.(participantIdentity);
    // Fail closed: exclude unless the server confirms co-occupancy AND we have a
    // resolvable room id to key the password gate on. Never skip the password check
    // on a malformed/empty area id (that would silently treat a locked room as open).
    if (!serverConfirmsCoOccupant || !knownRoomId) {
      return false;
    }
    return isPasswordOkForVirtualRoomPair(knownRoomId, mySid, participantIdentity, virtualRoomFilters);
  }

  const roomFilter = myArea ? virtualRoomFilters[myArea.roomId] : undefined;
  const myAuthorized = roomFilter?.hasPassword
    ? !!(mySid && roomFilter.authorizedSessionIds.includes(mySid))
    : true;

  if (roomFilter?.hasPassword && !myAuthorized) {
    return false;
  }

  const resolved = resolveSameVirtualRoomMedia(game, participantIdentity, virtualRoomFilters);
  if (resolved.sameRoomMedia && !resolved.passwordOk) {
    return false;
  }
  if (resolved.shouldSubscribeMedia) {
    return true;
  }

  if (options?.isScreenSharing && resolved.sameRoomMedia && resolved.passwordOk) {
    return true;
  }

  if (hasServerVirtualRoomCoOccupancy(game, participantIdentity, virtualRoomFilters)) {
    return true;
  }



  const network = game.network;
  if (network?.isVirtualRoomCoOccupant?.(participantIdentity)) {
    const myServerVr = getLocalServerVirtualRoomId(game);
    const myDeclaredVr = myArea?.roomId ?? null;
    const filterRoomId = myServerVr || myDeclaredVr;
    if (filterRoomId) {
      return isPasswordOkForVirtualRoomPair(
        filterRoomId,
        mySid,
        participantIdentity,
        virtualRoomFilters
      );
    }
    return true;
  }

  // Server-enforced isolation: exclude when our own server VR is known and differs from
  // theirs. Must use the same lenient predicate as useProximityState (SC-273 AC3) — a
  // strict `!==` here would exclude participants that the proximity check includes (because
  // shouldExcludeByServerVirtualRoom defers when our own VR is null), leaving tiles visible
  // but un-positioned by the Phaser scene callback, which causes speaking-triggered
  // re-renders to flash stale React-state positions (overlay tile oscillation).
  const myServerVr = getLocalServerVirtualRoomId(game);
  const otherServerVr = getRemoteServerVirtualRoomId(game, participantIdentity);
  if (shouldExcludeByServerVirtualRoom(myServerVr, otherServerVr)) {
    return false;
  }

  if (options?.settleGraceActive) {
    return true;
  }

  if (!otherPos) {
    return false;
  }

  const myPlayerObj = {
    x: myPlayer.x,
    y: myPlayer.y,
    playerId: myPlayer.playerId,
  };
  const otherPlayerObj = {
    x: otherPos.x,
    y: otherPos.y,
    playerId: otherPos.playerId,
  };

  return canPlayersCommunicate(myPlayerObj, otherPlayerObj, DEFAULT_PROXIMITY_FALLBACK_PX);
}
